import { UseSeller } from '@/contexts/seller/types'
import { SellerData } from '@/proxy/seller/hooks/useSellerIsBlocked/types'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useSellerIsBlocked } from '@/proxy/seller/hooks'
import POC_ALL_STATUS from '@/resources/enums/pocAllPossibleStatus'
import { REQUEST_ERRORS_CODES } from '@/resources/enums/errors/requestErrors'
import { isAuthenticated } from '@/resources/services/permission/auth'
import { SELLER_DATA } from '@/resources/utils'
const {
  ONBOARDING,
  ASSISTED_OPERATIONAL,
  OPERATIONAL,
  REJECTED,
  NOT_VALIDATED,
  REGISTERED,
} = POC_ALL_STATUS

export const SellerContext = createContext<UseSeller>({
  isLoadingSeller: true,
  isFetchedSeller: false,
  sellerData: null,
  isOperating: false,
  isPocCBE: false,
  isTrainingOrOperating: false,
  isRejectedOrInvalidated: false,
  isRegistered: false,
  isRegisteredWithoutMainPhoneNumber: false,
  isCircuitOpen: false,
  hasTwinSeller: false,
  deliveryMethods: [],
  role: '',
})

export const SellerProvider = (props: PropsWithChildren) => {
  const [responseSellerData, setResponseSellerData] =
    useState<SellerData | null>(null)

  const {
    isLoading: isLoadingSeller = false,
    isFetched: isFetchedSeller = false,
    data,
    refetch,
  } = useSellerIsBlocked()

  const sellerData: SellerData | null = data ?? null

  const role = sellerData?.user?.role ?? ''

  const isCircuitOpen =
    sellerData?.error?.code === REQUEST_ERRORS_CODES.CIRCUIT_IS_OPEN

  useEffect(() => {
    if (isLoadingSeller) return

    if (!isCircuitOpen) {
      setResponseSellerData(sellerData)
      localStorage.setItem(SELLER_DATA, JSON.stringify(sellerData))

      return
    }

    const storageData = localStorage.getItem(SELLER_DATA)
    if (storageData) {
      const parsedData = JSON.parse(storageData) as SellerData
      setResponseSellerData(parsedData)
    }
  }, [isCircuitOpen, isLoadingSeller, sellerData])

  const isOperating =
    responseSellerData?.seller?.status === ASSISTED_OPERATIONAL ||
    responseSellerData?.seller?.status === OPERATIONAL

  const isTrainingOrOperating =
    responseSellerData?.seller?.status === ONBOARDING ||
    responseSellerData?.seller?.status === ASSISTED_OPERATIONAL ||
    responseSellerData?.seller?.status === OPERATIONAL

  const isRejectedOrInvalidated =
    responseSellerData?.seller?.status === REJECTED ||
    responseSellerData?.seller?.status === NOT_VALIDATED

  const isRegistered = responseSellerData?.seller?.status === REGISTERED

  const isRegisteredWithoutMainPhoneNumber =
    responseSellerData?.seller?.status === REGISTERED &&
    responseSellerData?.seller?.mainPhoneNumber === null

  const hasTwinSeller = !!responseSellerData?.seller?.twinSellerId
  const deliveryMethods = useMemo(
    () =>
      responseSellerData?.seller?.shop?.deliveryMethods?.map(
        ({ type }) => type
      ) ?? [],
    [responseSellerData]
  )

  const isPocCBE = responseSellerData?.seller?.category === 'CBE'

  const context = useMemo(
    () => ({
      isLoadingSeller,
      isFetchedSeller,
      sellerData: responseSellerData,
      isOperating,
      isPocCBE,
      isTrainingOrOperating,
      isRejectedOrInvalidated,
      isRegistered,
      isRegisteredWithoutMainPhoneNumber,
      isCircuitOpen,
      hasTwinSeller,
      deliveryMethods,
      role,
      refetch,
    }),
    [
      isLoadingSeller,
      isFetchedSeller,
      responseSellerData,
      isOperating,
      isPocCBE,
      isTrainingOrOperating,
      isRejectedOrInvalidated,
      isRegistered,
      isRegisteredWithoutMainPhoneNumber,
      isCircuitOpen,
      hasTwinSeller,
      deliveryMethods,
      role,
      refetch,
    ]
  )

  return <SellerContext.Provider value={context} {...props} />
}

export const useSeller = (): UseSeller => {
  const context = useContext(SellerContext)
  if (isAuthenticated() && !context) {
    throw new Error('useSeller must be used within a SellerContext')
  }
  return context
}
